import { useEffect, useRef } from "react";
import { atom, useAtomValue } from "jotai";
import { useQueryClient } from "@tanstack/react-query";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { useCompositionForm } from "./useCompositionForm";
import { useCurrentComposition } from "./useCurrentComposition";

const DELIVERY_SYNC_DEBOUNCE_MS = 1500;

/**
 * Atom incremented every time a quantity changes in the Design Lab.
 * SizeSelector.changeQuantity bumps this counter.
 */
export const quantityChangedAtom = atom(0);

type FastestDeliveryStockData = {
  enabled: boolean;
  availableCompositionIds: number[];
  variantStockCounts: Record<string, number>;
};

/**
 * Build a variants hash from all addedCompositions.
 */
function buildVariantsHash(
  addedCompositions: Array<{ id: string; catalogId: string; variants?: Record<string, number> }>
): { hash: Record<string, string>; hasVariants: boolean } {
  const hash: Record<string, string> = {};
  let hasVariants = false;

  addedCompositions.forEach(comp => {
    if (!comp.variants) return;
    Object.entries(comp.variants).forEach(([variantId, quantity]) => {
      if (quantity > 0) {
        const existing = hash[variantId] ? parseInt(hash[variantId], 10) : 0;
        hash[variantId] = String(existing + quantity);
        hasVariants = true;
      }
    });
  });

  return { hash, hasVariants };
}

/**
 * Check if ANY composition across ALL products has slow stock by reading
 * cached fastest-delivery-stock data from the React Query cache.
 */
function computeHasSlowStock(
  addedCompositions: Array<{ id: string; catalogId: string; variants?: Record<string, number> }>,
  queryClient: ReturnType<typeof useQueryClient>
): boolean {
  // Group compositions by catalogId to look up per-product stock data
  const catalogIds = [...new Set(addedCompositions.map(c => c.catalogId).filter(Boolean))];

  // Read cached stock data for each product
  const stockByProduct = new Map<string, FastestDeliveryStockData | undefined>();
  catalogIds.forEach(catalogId => {
    const data = queryClient.getQueryData<FastestDeliveryStockData>([
      "fastestDeliveryStock",
      catalogId
    ]);
    stockByProduct.set(catalogId, data);
  });

  return addedCompositions.some(composition => {
    const stockData = stockByProduct.get(composition.catalogId);
    if (!stockData?.enabled) return false;

    const hasQuantities = Object.values(composition.variants || {}).some(qty => qty > 0);
    if (!hasQuantities) return false;

    const compositionIdNum = parseInt(String(composition.id), 10);
    if (!stockData.availableCompositionIds.includes(compositionIdNum)) {
      return true;
    }

    return Object.entries(composition.variants || {}).some(([variantId, quantity]) => {
      if (quantity <= 0) return false;
      const optimalStock = stockData.variantStockCounts[String(variantId)] ?? 0;
      return quantity > optimalStock;
    });
  });
}

/**
 * Watches for quantity changes across ALL products and triggers a delivery
 * estimate refetch when the slow-stock boundary is crossed.
 *
 * - **On page load** (edit design): fetches immediately if quantities exist.
 * - **On boundary crossing**: when ANY variant of ANY product goes in or out
 *   of optimal DC stock, fetches updated delivery estimates.
 * - Sends ALL variants from ALL compositions in a single API call.
 * - The result is a single delivery date (the backend computes the max).
 */
export function useDeliveryDateSync(): void {
  const { form } = useCompositionForm();
  const { currentComposition } = useCurrentComposition();
  const queryClient = useQueryClient();

  const quantityVersion = useAtomValue(quantityChangedAtom);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFetchingRef = useRef(false);
  const pendingFetchRef = useRef<(() => void) | null>(null);
  const hasInitialFetchedRef = useRef(false);
  const prevSlowStockRef = useRef<boolean | null>(null);
  const prevCompositionIdRef = useRef<string | null>(null);

  useEffect(() => {
    const compositionId = currentComposition?.id ?? form.getValues("currentCompositionId");
    if (!compositionId) return undefined;

    const addedCompositions = form.getValues("addedCompositions");
    const { hash: variantsHash, hasVariants } = buildVariantsHash(addedCompositions);
    if (!hasVariants) return undefined;

    const isInitialFetch = !hasInitialFetchedRef.current;

    // Detect composition (color) change.
    // Do NOT update prevCompositionIdRef here — defer to doFetch so that
    // if the debounce timer is cancelled, the change is re-detected.
    const prevCompositionId = prevCompositionIdRef.current;
    const compositionChanged =
      prevCompositionId !== null && prevCompositionId !== String(compositionId);

    // Check slow stock across ALL products using cached data
    const hasSlowStock = computeHasSlowStock(addedCompositions, queryClient);

    // Detect slow-stock boundary crossing.
    // Do NOT update prevSlowStockRef here — defer to doFetch so that
    // if the debounce timer is cancelled by a subsequent effect run,
    // the ref still holds the old value and the boundary change is re-detected.
    const prevSlowStock = prevSlowStockRef.current;
    const boundaryChanged = prevSlowStock !== null && prevSlowStock !== hasSlowStock;

    // Only fetch when:
    // 1. Initial page load with existing quantities (edit design)
    // 2. Slow-stock boundary crossed for ANY variant of ANY product
    // 3. Composition (color) changed — delivery dates are per-composition
    if (!isInitialFetch && !boundaryChanged && !compositionChanged) return undefined;

    const doFetch = () => {
      if (isFetchingRef.current) {
        // Queue this fetch so it runs after the in-flight request completes.
        // This prevents dropped updates when a color change occurs during
        // an in-flight request (e.g. initial load fetch).
        pendingFetchRef.current = doFetch;
        return;
      }
      pendingFetchRef.current = null;
      isFetchingRef.current = true;
      hasInitialFetchedRef.current = true;
      prevSlowStockRef.current = hasSlowStock;
      prevCompositionIdRef.current = String(compositionId);

      // Send ALL variants from ALL compositions in a single call
      const variantsParam = encodeURIComponent(JSON.stringify(variantsHash));
      const url = `/api/made/current_pdp_date?composition_id=${compositionId}&variants_hash=${variantsParam}`;

      fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json" }
      })
        .then(r => r.json())
        .then(objNamesToCamelCase)
        .then(res => {
          if (res?.success && res.compositionDates) {
            const dates = res.compositionDates;

            // When multiple shipments exist, valueFull is "Feb 13 & Feb 18".
            // Show only the earliest (first) date in the header.
            if (dates.valueFull && dates.valueFull.includes("&")) {
              dates.valueFull = dates.valueFull.split("&")[0].trim();
            }

            queryClient.setQueryData(["deliveryEstimates"], dates);
          }
        })
        .catch(() => {
          // Silently fail
        })
        .finally(() => {
          isFetchingRef.current = false;
          const pending = pendingFetchRef.current;
          if (pending) {
            pendingFetchRef.current = null;
            pending();
          }
        });
    };

    // On page load (initial), fetch immediately
    if (isInitialFetch) {
      doFetch();
      return undefined;
    }

    // On boundary crossing, debounce the fetch
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(doFetch, DELIVERY_SYNC_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      pendingFetchRef.current = null;
    };
  }, [quantityVersion, currentComposition?.id, form, queryClient]);
}
